import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import UserService, { CHAVES_USUARIO } from '../api/UserService';
import { getApiErrorMessage } from '../utils/http';

/**
 * Autorização para os dados de saúde da pele (LGPD art. 11, I) — o consentimento próprio,
 * separado dos Termos, que libera anamnese, fotos, registros, progresso e a IA. Sem ela o
 * backend responde 403 nessas rotas. Mesma chave do usuário que Termos e Configurações já
 * usam: o portão não custa requisição a mais.
 */
export default function useSensitiveDataConsent() {
  const { userId } = useCurrentUser();
  const [salvando, setSalvando] = useState<boolean>(false);
  const [erro, setErro] = useState<string>('');

  const query = useQuery({
    queryKey: CHAVES_USUARIO.porId(userId!),
    queryFn: async () => (await UserService.getUserById(userId!)).data,
    enabled: Boolean(userId),
  });

  /** Só fecha depois que o servidor confirmou: autorização otimista é autorização que não existe. */
  const executar = async (acao: () => Promise<unknown>, falha: string): Promise<boolean> => {
    setSalvando(true);
    setErro('');
    try {
      await acao();
      return true;
    } catch (err: unknown) {
      setErro(getApiErrorMessage(err, falha));
      return false;
    } finally {
      setSalvando(false);
    }
  };

  return {
    autorizado: Boolean(query.data?.sensitiveDataConsentAt),
    // `isSuccess`: falha de rede não tranca o app atrás do portão.
    precisaAutorizar: query.isSuccess && !query.data?.sensitiveDataConsentAt,
    autorizar: () =>
      executar(UserService.grantSensitiveDataConsent, 'Não foi possível registrar sua autorização. Tente novamente.'),
    revogar: () =>
      executar(UserService.revokeSensitiveDataConsent, 'Não foi possível revogar agora. Nada foi apagado; tente novamente.'),
    salvando,
    erro,
  };
}
